import { Button, Form, Header, Label, List, Segment } from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import { PROTOCOLE_IMPAYE_ORMC, PROTOCOLE_IMPAYE_PES_ASAP } from '@/constants/regie';
import type { CycleType } from '@/types';
import { isValidationOrdonnateurDisabled, isValidationPosteComptableDisabled } from '@/utils';

type TransfertImpayesCyclesProps = {
  cycles: CycleType[];
  protocole?: string;
  loading?: boolean;
  onOrdonnateurValidate: (identifiantCycle: number) => () => void;
  onPosteComptableValidate: (identifiantCycle: number) => () => void;
};

const TransfertImpayesCycles = (props: TransfertImpayesCyclesProps) => {
  const { cycles, protocole, loading, onOrdonnateurValidate, onPosteComptableValidate } = props;

  return (
    <>
      <Header as="h3">
        <Header.Content>
          {_.size(cycles) === 1
            ? 'Cycle de facturation sélectionné'
            : 'Cycles de facturation sélectionnés'}
          <Header.Subheader style={{ marginTop: '0.5em' }}>
            {protocole === PROTOCOLE_IMPAYE_ORMC ? (
              'Après transfert et validation du titre global, les factures impayées supérieures au seuil de recouvrement seront transférées en trésorerie par rôle (ORMC).'
            ) : (
              <>
                Les factures impayées
                {_.size(cycles) === 1 ? ' du cycle suivant ' : ' des cycles suivants '}
                seront transférées en comptabilité par titrage individuel (PES ASAP).
              </>
            )}
          </Header.Subheader>
        </Header.Content>
      </Header>
      <List>
        {_.map(cycles, (cycle) => {
          const {
            identifiant,
            titre,
            dateEmission,
            logicielFacturier,
            nombreFacturesImpayes,
            validationOrdonnateur,
            dateTransfertImpaye,
            dateTransfertTitre,
            dateValidationPosteComptable,
          } = cycle;

          const validationOrdonnateurDisabled = isValidationOrdonnateurDisabled(
            protocole || '',
            cycle,
          );
          const validationPosteComptableDisabled = isValidationPosteComptableDisabled(
            protocole || '',
            cycle,
          );

          return (
            <Segment key={`cycle@${identifiant}`}>
              <List.Item>
                <List.Header as="h5" style={{ display: 'flex', alignItems: 'center' }}>
                  <Header.Content style={{ width: 400 }}>
                    {titre}{' '}
                    <span
                      style={{ opacity: 0.5 }}
                    >{`(${dayjs(dateEmission).format('DD/MM/YYYY')})`}</span>
                  </Header.Content>
                  <Label basic circular>
                    {logicielFacturier.nom}
                  </Label>
                  <Header.Content style={{ marginLeft: '5em' }}>
                    {nombreFacturesImpayes === 1
                      ? `1 facture impayée${dateValidationPosteComptable ? ' validée' : ''}`
                      : `${nombreFacturesImpayes} factures impayées${dateValidationPosteComptable ? ' validées' : ''}`}
                  </Header.Content>
                  <Form.Group
                    inline
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginLeft: 'auto',
                      marginBottom: 0,
                      height: 30,
                      minWidth: 475,
                    }}
                  >
                    {(protocole === PROTOCOLE_IMPAYE_PES_ASAP &&
                      (_.isNil(dateTransfertImpaye) || validationOrdonnateur)) ||
                    (protocole === PROTOCOLE_IMPAYE_ORMC &&
                      (_.isNil(dateTransfertTitre) || validationOrdonnateur)) ? (
                      <Form.Checkbox
                        label={
                          protocole === PROTOCOLE_IMPAYE_ORMC
                            ? 'Validation titre global'
                            : 'Validation ordonnateur'
                        }
                        checked={validationOrdonnateur}
                        onChange={onOrdonnateurValidate(identifiant)}
                        disabled={validationOrdonnateurDisabled || loading}
                      />
                    ) : (
                      <Button
                        content={
                          protocole === PROTOCOLE_IMPAYE_ORMC
                            ? 'Validation titre global'
                            : 'Validation ordonnateur'
                        }
                        positive
                        onClick={onOrdonnateurValidate(identifiant)}
                        disabled={validationOrdonnateurDisabled || loading}
                        loading={loading}
                      />
                    )}
                    {!validationOrdonnateur || !_.isEmpty(dateValidationPosteComptable) ? (
                      <Form.Checkbox
                        label="Validation poste comptable"
                        checked={!_.isEmpty(dateValidationPosteComptable)}
                        onChange={onPosteComptableValidate(identifiant)}
                        disabled={validationPosteComptableDisabled || loading}
                      />
                    ) : (
                      <Button
                        content="Validation poste comptable"
                        positive
                        onClick={onPosteComptableValidate(identifiant)}
                        disabled={validationPosteComptableDisabled || loading}
                        loading={loading}
                      />
                    )}
                  </Form.Group>
                </List.Header>
              </List.Item>
            </Segment>
          );
        })}
      </List>
    </>
  );
};

export default TransfertImpayesCycles;
